import { LearnLayout } from "./Layout/LearnLayout";

export const GlossaryLearn = () => {
  return (
    <LearnLayout>
      <h3>Glossary</h3>
      <p>
        A reference guide to the key terms, concepts, and methods you'll
        encounter while using Vojomo.
      </p>

      <h4>The Platform</h4>
      <p>
        <strong>Vojomo</strong> — The strategic operating platform. Vojomo helps
        founders and leadership teams build, run, and evolve their company
        strategy in one place.
      </p>
      <p>
        <strong>Vo</strong> — Vojomo's AI thought partner. Vo researches,
        synthesizes, and writes alongside you throughout the platform. Think of
        Vo as a knowledgeable collaborator who helps you think more clearly and
        move faster.
      </p>
      <p>
        <strong>VoChat</strong> — A streamlined interface for talking directly
        with Vo. Use it to ask questions, explore ideas, or get help with
        anything across your strategy work.
      </p>

      <h4>The Roadmap</h4>
      <p>
        <strong>Roadmap</strong> — The central strategic artifact in Vojomo. A
        roadmap captures where your company is going, why it exists, and what it
        needs to accomplish. Each roadmap is built from seven Building Blocks.
      </p>
      <p>
        <strong>Mountain View</strong> — A visual representation of your roadmap
        that displays all seven Building Blocks as a mountain diagram. Accessible
        from the Roadmap Builder session. Supports inline editing.
      </p>
      <p>
        <strong>Building Block</strong> — One of the seven strategic components
        that make up a complete roadmap. Building Blocks are filled in sequence
        to create a coherent, connected strategy.
      </p>
      <p>
        <strong>Session</strong> — A working session inside the Roadmap Builder
        where you and Vo develop one or more Building Blocks together.
      </p>
      <p>
        <strong>Block</strong> — A single unit within a roadmap. Progress is
        tracked as "blocks completed" (e.g. 4 / 7).
      </p>
      <p>
        <strong>Gap</strong> — An unfilled or incomplete section within a
        roadmap. Gaps are flagged so you know what still needs to be addressed.
      </p>
      <p>
        <strong>Assumption</strong> — A piece of content that Vo has reasoned
        and filled in on your behalf, based on available context. Assumptions
        are clearly labeled and should be reviewed.
      </p>
      <p>
        <strong>Draft</strong> — A roadmap or workflow that has been created but
        not yet finalized or activated.
      </p>

      <h4>The Seven Building Blocks</h4>
      <p>
        <strong>Vision</strong> — A description of the future that begins with
        "We envision a world where…" — articulating what the world looks like
        when the problem you're solving no longer exists.
      </p>
      <p>
        <strong>Mission</strong> — A clear statement of what your company does
        today to advance your vision.
      </p>
      <p>
        <strong>Peak Ambition</strong> — A realistic yet ambitious future
        headline that conceptualizes your company's greatest achievement, paired
        with a specific future date and publication name.
      </p>
      <p>
        <strong>Peak Ambition Objectives</strong> — A small set of specific,
        measurable outcomes that must be true in order to achieve your Peak
        Ambition.
      </p>
      <p>
        <strong>3 Year Objectives</strong> — Measurable outcomes your company
        must accomplish within the next three years to stay on track toward your
        Peak Ambition.
      </p>
      <p>
        <strong>1 Year Objectives</strong> — The specific outcomes your company
        needs to achieve this year to progress toward your 3 Year Objectives.
      </p>
      <p>
        <strong>Quarterly Objectives</strong> — Also called "Next Quarter." The
        immediate outcomes your team is focused on this quarter to advance your
        annual goals.
      </p>

      <h4>Next Quarter Objectives (NQO)</h4>
      <p>
        <strong>Next Quarter Objectives (NQO)</strong> — An always-on way to
        plan what must be true in the next quarter. Found inside the Quarterly
        Objectives building block. Save your NQO at any time — no setup
        required. Vo can convert your notes into clear, measurable objectives.
      </p>
      <p>
        <strong>Quarter Navigator</strong> — The prev/next control in the NQO
        view for moving between quarters to review history or plan ahead.
      </p>

      <h4>Objectives Framework</h4>
      <p>
        <strong>SMART Objectives</strong> — Specific, Measurable, Achievable,
        Relevant, Time-based. All objectives on Vojomo follow this framework to
        ensure clarity and accountability.
      </p>
      <p>
        <strong>"What must be true?"</strong> — The guiding question for writing
        strong objectives. Rather than asking "what will we do?", Vojomo
        encourages teams to ask what must be true (measured outcomes) in order
        to reach the next level of the strategy.
      </p>

      <h4>Studio & Documents</h4>
      <p>
        <strong>Studio</strong> — The section of Vojomo where your content
        artifacts live: documents, alignment reports, and progress syntheses.
        Access it from the main navigation.
      </p>
      <p>
        <strong>Artifact</strong> — Any piece of content produced in or
        associated with a session — including text documents, alignment reports,
        and progress syntheses.
      </p>
      <p>
        <strong>Alignment Report</strong> — An AI-generated report that
        synthesizes your team's contributor scores and notes into a structured
        comparison, helping leadership see where the team aligns or diverges.
      </p>
      <p>
        <strong>Progress Synthesis</strong> — An AI-generated summary of
        progress data for a period, compiled from team contributions and
        released by the session owner or advisor.
      </p>
      <p>
        <strong>Document</strong> — Any piece of written content in Vojomo.
        Documents can be Topics or Briefing Docs, and can be organized into
        folders.
      </p>
      <p>
        <strong>Topic</strong> — The primary document type. A Topic has a rich
        text editor and is organized into tabs: Topic (content), Sources,
        Roadmap, and Briefing Doc.
      </p>
      <p>
        <strong>Briefing Doc</strong> — A structured summary document type,
        often derived from a Topic, used for sharing or presenting information.
      </p>
      <p>
        <strong>Source</strong> — A reference file or URL attached to a
        document. Sources give Vo context and grounding when generating content.
      </p>
      <p>
        <strong>Output</strong> — Content that Vo has generated in response to
        a prompt or workflow task.
      </p>
      <p>
        <strong>Folder</strong> — An organizational container for grouping
        related documents in your Document Library.
      </p>

      <h4>After Action Review (AAR)</h4>
      <p>
        <strong>After Action Review (AAR)</strong> — A structured reflection
        tool for capturing learnings after an event or project. Guides
        participants through a set of questions to surface what worked, what
        didn't, and what to do differently next time.
      </p>

      <h4>Roadmap Import &amp; Research</h4>
      <p>
        <strong>Roadmap Import</strong> — A way to start a new roadmap session
        from existing content. Accepts JSON, PDF, and plain text. Vo extracts
        the building block structure and pre-fills the session.
      </p>
      <p>
        <strong>Research</strong> — Vo's background research feature in the
        Roadmap Builder. Triggered via the Globe icon. Vo gathers and synthesizes
        context for your roadmap. Research runs are tracked in a history log with
        summaries you can review at any time.
      </p>

      <h4>Collaboration</h4>
      <p>
        <strong>Shared Roadmap</strong> — A roadmap that another user has
        invited you to collaborate on. Shared roadmaps appear in your Home view
        under "Roadmaps others have invited you to collaborate on."
      </p>
      <p>
        <strong>Contribution / Solo Contribution</strong> — Your individual
        input to a shared roadmap. A Solo Contribution is your personal version
        of a roadmap block that gets synthesized with others'.
      </p>
      <p>
        <strong>Collaborator</strong> — Any user who has been given access to a
        roadmap or document.
      </p>
      <p>
        <strong>Owner</strong> — The user who created a roadmap or document.
        Owners have full control over sharing and editing.
      </p>

      <h4>Roles & Access</h4>
      <p>
        <strong>Owner</strong> — The person who created a roadmap, or a
        collaborator invited or promoted to co-owner. Full control: edit
        everything, invite others, run synthesis, lock periods, and publish. One
        of the two roles you can pick in the invite dropdown.
      </p>
      <p>
        <strong>Advisor</strong> — A user assigned to advise on a roadmap
        session. Advisors can always see all contributor data regardless of
        visibility settings. Sessions you're advising appear under "Sessions I'm
        Advising" on your home screen.
      </p>
      <p>
        <strong>Team Member</strong> — A collaborator with full edit rights on a
        shared roadmap. Team Members can edit building blocks, view who else is
        collaborating, and see alignment reports and pending contributions. It's
        the default of the two invite-dropdown roles (Owner or Team Member).
        Roadmaps where you are a Team Member appear in the "Team Roadmaps"
        section of My Roadmaps.
      </p>
      <p>
        <strong>Contributor</strong> <em>(legacy)</em> — A collaborator who
        submits their own version of the roadmap building blocks, synthesized by
        the session owner. No longer offered when inviting someone new (the
        invite dropdown is now just Owner and Team Member); existing Contributors
        keep their access.
      </p>
      <p>
        <strong>Reviewer</strong> <em>(legacy)</em> — A read-focused collaborator
        ("Can view &amp; comment" or "View only"). No longer offered when inviting
        someone new; existing Reviewers keep their access.
      </p>
      <p>
        <strong>Member</strong> — A standard platform user within a company.
      </p>
      <p>
        <strong>Company Admin</strong> — A user with administrative access to
        manage company members and settings.
      </p>
      <p>
        <strong>View</strong> — Read-only access to a roadmap or document.
      </p>
      <p>
        <strong>Comment</strong> — Access to read and leave comments on a
        roadmap or document.
      </p>
      <p>
        <strong>Write</strong> — Full edit access to a roadmap or document.
      </p>

      <h4>Operating Tools</h4>
      <p>
        <strong>Operating Tools</strong> — The section of Vojomo that helps
        teams track progress and performance against their strategy. Includes
        QBR Scoring and Weekly Progress.
      </p>
      <p>
        <strong>QBR (Quarterly Business Review)</strong> — A structured review
        of how well the team is tracking against annual objectives. QBR Scoring
        uses a 0–10 scale (representing 0%–100% progress).
      </p>
      <p>
        <strong>On Track</strong> — A QBR scoring status indicating the
        objective is progressing at or above the expected rate.
      </p>
      <p>
        <strong>Behind</strong> — A QBR scoring status indicating the objective
        is slightly below the expected pace.
      </p>
      <p>
        <strong>Far Behind</strong> — A QBR scoring status indicating
        significant underperformance against the target.
      </p>
      <p>
        <strong>Red Flag</strong> — The lowest QBR scoring status. Indicates
        the objective is critically off-track and needs immediate attention.
      </p>
      <p>
        <strong>Weekly Progress</strong> — A tool for tracking short-term
        momentum and activity against objectives on a week-by-week basis.
      </p>

      <h4>Coordinator & Workflows</h4>
      <p>
        <strong>Coordinator</strong> — The section of Vojomo for managing
        structured workflows across your team.
      </p>
      <p>
        <strong>Workflow</strong> — A structured assignment sent to participants
        to complete a specific task (e.g. QBR Scoring, Roadmap Building, Topic
        Document). Workflows have three statuses: Draft, Active, and Completed.
      </p>
      <p>
        <strong>Participant</strong> — A user assigned to complete a workflow.
        Participant statuses: Pending, Completed, Overdue.
      </p>
      <p>
        <strong>Module</strong> — The type of task a workflow is built around.
        Current modules: QBR Scoring, Roadmap Builder, Topic Document.
      </p>
      <p>
        <strong>Assignment</strong> — The specific task given to a participant
        in a workflow, which may include a URL linking to the relevant tool or
        content.
      </p>
      <p>
        <strong>Deadline</strong> — The date by which a workflow participant is
        expected to complete their assignment. Participants without a completed
        submission past this date are marked Overdue.
      </p>
    </LearnLayout>
  );
};
